import React, { useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faEnvelope,
  faSpinner
} from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'
import { LoadingSpinner } from '../atoms'
import styles from './EmailSendingProgressDialog.module.scss'

const EmailSendingProgressDialog = ({
  open,
  onClose,
  automationName,
  emailProgress = [],
  isComplete = false,
  totalEmails = 0,
  progress = {},
  error = null,
  onRetry
}) => {
  const currentProgress = useMemo(() => {
    if (emailProgress.length > 0) {
      return emailProgress.filter(email =>
        email.status === 'sent' || email.status === 'failed'
      ).length
    }
    return 0
  }, [emailProgress])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
      case 'failed':
        return <FontAwesomeIcon icon={faExclamationTriangle} className={styles.errorIcon} />
      case 'sending':
        return <FontAwesomeIcon icon={faSpinner} className={styles.sendingIcon} spin />
      case 'no_recipients':
        return <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ff9800' }} />
      default:
        return <FontAwesomeIcon icon={faClock} className={styles.pendingIcon} />
    }
  }

  const getStatusText = (status, error, reason, recipientsList = [], isTestMode = false, actualRecipient = null) => {
    switch (status) {
      case 'sent':
        if (isTestMode && actualRecipient) {
          return recipientsList.length > 0
            ? `Test email sent to: ${actualRecipient} (would have sent to: ${recipientsList.join(', ')})`
            : `Test email sent to: ${actualRecipient}`
        }
        return recipientsList.length > 0
          ? `Sent successfully to: ${recipientsList.join(', ')}`
          : 'Sent successfully'
      case 'failed':
        if (isTestMode && actualRecipient) {
          return recipientsList.length > 0
            ? `Failed to send test email to: ${actualRecipient} (would have sent to: ${recipientsList.join(', ')})`
            : `Failed to send test email to: ${actualRecipient}`
        }
        return error || 'Failed to send'
      case 'sending':
        return isTestMode ? 'Sending test email...' : 'Sending...'
      case 'no_recipients':
        return reason || 'No recipients with email addresses found'
      default:
        return 'Pending'
    }
  }

  const sentCount = emailProgress.filter(email => email.status === 'sent').length
  const failedCount = emailProgress.filter(email => email.status === 'failed').length
  const noRecipientsCount = emailProgress.filter(email => email.status === 'no_recipients').length
  const pendingCount = emailProgress.filter(email =>
    email.status === 'pending' || email.status === 'sending'
  ).length

  const progressPercentage = progress.progress || 0

  return (
    <Dialog
      open={open}
      onClose={(isComplete || error) ? onClose : undefined}
      maxWidth="md"
      fullWidth
      disableBackdropClick={!isComplete && !error}
      disableEscapeKeyDown={!isComplete && !error}
      PaperProps={{ style: { backgroundColor: 'var(--color-card-bg)', color: 'var(--color-text-primary)' } }}
    >
      <DialogTitle style={{ color: 'var(--color-text-primary)' }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <FontAwesomeIcon icon={faEnvelope} style={{ color: '#2196f3' }} />
          Email Sending Progress - {automationName}
        </Box>
      </DialogTitle>

      <DialogContent className={styles.progressContainer}>
        <Box className={styles.statusHeader}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {isComplete ? 'Email Sending Complete' : (error ? 'Email Sending Failed' : 'Sending Emails...')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {progress.message || (isComplete
                ? `Finished processing ${totalEmails} email${totalEmails !== 1 ? 's' : ''}`
                : `Processing ${totalEmails} email${totalEmails !== 1 ? 's' : ''} for automation`
              )}
            </Typography>
          </Box>
          {!isComplete && (
            <LoadingSpinner size={40} />
          )}
        </Box>

        <Box className={styles.progressStats}>
          <Chip
            label={`${sentCount} Sent`}
            color="primary"
            variant={sentCount > 0 ? "default" : "outlined"}
            className={styles.statChip}
          />
          <Chip
            label={`${failedCount} Failed`}
            color="secondary"
            variant={failedCount > 0 ? "default" : "outlined"}
            className={styles.statChip}
          />
          <Chip
            label={`${noRecipientsCount} No Recipients`}
            style={noRecipientsCount > 0 ? { backgroundColor: '#ff9800', color: 'white' } : {}}
            variant={noRecipientsCount > 0 ? "default" : "outlined"}
            className={styles.statChip}
          />
          <Chip
            label={`${pendingCount} Pending`}
            variant="outlined"
            className={styles.statChip}
          />
        </Box>

        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="textSecondary">
              Progress: {currentProgress} of {totalEmails}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {Math.round(progressPercentage)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            style={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {emailProgress.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
              Email Status Details:
            </Typography>
            <List dense>
              {emailProgress.map((email, index) => (
                <ListItem key={`${email.campaign_name}-${email.recipient || index}`} className={styles.emailItem}>
                  <ListItemIcon>
                    {getStatusIcon(email.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box className={styles.emailStatus}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {email.campaign_name}
                        </Typography>
                        {email.recipient && (
                          <Typography variant="caption" color="textSecondary">
                            → {email.recipient}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        {getStatusText(email.status, email.error, email.reason, email.recipients_list, email.is_test_mode, email.actual_recipient)}
                        {email.timestamp && (
                          <span> • {new Date(email.timestamp).toLocaleTimeString()}</span>
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {emailProgress.length === 0 && !isComplete && (
          <LoadingSpinner message={progress.message || 'Preparing to send emails...'} />
        )}

        {error && (
          <Box
            p={2}
            style={{ background: 'rgba(244, 67, 54, 0.1)', border: '1px solid rgba(244, 67, 54, 0.2)', borderRadius: 4 }}
            mb={2}
          >
            <Typography variant="body2" style={{ color: '#ef5350' }}>
              <strong>Error:</strong> {error}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {isComplete && failedCount > 0 && onRetry && (
          <Button
            onClick={onRetry}
            color="secondary"
            variant="outlined"
          >
            Retry Failed ({failedCount})
          </Button>
        )}
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          disabled={!isComplete && !error}
        >
          {isComplete ? 'Close' : error ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

EmailSendingProgressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automationName: PropTypes.string,
  emailProgress: PropTypes.arrayOf(PropTypes.shape({
    campaign_name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['pending', 'sending', 'sent', 'failed', 'no_recipients']).isRequired,
    error: PropTypes.string,
    reason: PropTypes.string,
    timestamp: PropTypes.string,
    recipients: PropTypes.number,
    recipients_list: PropTypes.arrayOf(PropTypes.string),
    success: PropTypes.bool
  })),
  isComplete: PropTypes.bool,
  totalEmails: PropTypes.number,
  progress: PropTypes.object,
  error: PropTypes.string,
  onRetry: PropTypes.func
}

export default EmailSendingProgressDialog
